import JSZip from 'jszip';

import { RCXLogger } from '../../logger';
import { FILE_PREFIX, EXPORT_LOG_TYPE, DEFAULT_TOKEN_TYPE } from '../constant';
import { CprParams, ISendProblemReportParamsToCpr, ZipParams, dateTypes } from '../types';

export type CPROptions = {
    token: string;
    cprUri: string;
    tokenType?: string;
};
class CPRClient {
    static singleton: CPRClient | undefined = undefined;
    private xUserAgent: string = '';

    static instance(): CPRClient {
        if (!CPRClient.singleton) {
            CPRClient.singleton = new CPRClient();
        }
        return CPRClient.singleton;
    }

    getTimeRange(date: string | number): number {
        let timeRange = typeof date === 'number' ? date * 24 * 60 * 60 * 1000 : 0;
        if (date === dateTypes.TODAY) {
            timeRange = 24 * 60 * 60 * 1000;
        }
        return timeRange;
    }

    getLogs(date: string | number) {
        const timeRange = this.getTimeRange(date);
        return timeRange
            ? RCXLogger.getInstance().getLogRecordsByTime(timeRange)
            : RCXLogger.getInstance().getAllLogRecords();
    }

    getZipData({ date }: ZipParams): Promise<CprParams> {
        return new Promise((resolve, reject) => {
            try {
                const zip = new JSZip();
                return this.getLogs(date)
                    .then((allLogs) => {
                        const fileBaseName = `${FILE_PREFIX}_${Date.now()}`;
                        zip.file(`${fileBaseName}.${EXPORT_LOG_TYPE}`, JSON.stringify(allLogs, null, 2), {
                            compression: 'DEFLATE',
                        });

                        return zip.generateAsync({ type: 'blob' }).then((blob) => {
                            resolve({
                                blob,
                                filename: `${fileBaseName}.zip`,
                            });
                        });
                    })
                    .catch((e) => {
                        reject(new Error(`zip error: ${e}`));
                    });
            } catch (e) {
                reject(new Error(`zip error: ${e}`));
                return void 0;
            }
        });
    }

    async formatSendFormData(date: string | number) {
        const zipData = await this.getZipData({
            date,
        });
        const { blob, filename } = zipData;
        const data = new FormData();
        try {
            data.append('attachments', blob, filename);
        } catch (e) {
            console.error(`render cpr data fail: ${e}`);
        }
        return data;
    }

    async sendAttachmentToCpr(params: ISendProblemReportParamsToCpr) {
        if (!params.token) {
            return null;
        }

        const sendData = await this.formatSendFormData(params.date);

        const controller = new AbortController();
        const abortSignal = controller.signal;
        return fetch(`${params.cprUri}/cpr-api/v1/problem-reports/${params.id}/attachments`, {
            method: 'POST',
            headers: {
                Authorization: `${params.tokenType || DEFAULT_TOKEN_TYPE} ${params.token}`,
                'User-Agent': window.navigator.userAgent,
                'X-User-Agent': this.xUserAgent,
            },
            body: sendData,
            signal: abortSignal,
        })
            .then((res) => {
                if (res.ok) {
                    console.log('======send attachments to CPR successfully!');
                } else {
                    throw res;
                }
            })
            .catch((e) => {
                console.log('==========send attachments to CPR failed!', e);
            });
    }
}

export { CPRClient };
